import { commonRequestData, fetchData } from "../../api";

/**
 * Obtiene la lista de cotizaciones llamando al endpoint correspondiente de la API.
 *
 * @param {Array} estados - Lista de estados opcional para filtrar las cotizaciones.
 * @returns {Promise<Object>} Promesa que resuelve con la respuesta de la API, que contiene la lista de cotizaciones.
 */
export const obtenerLista_Cotizaciones = async ({ estados }) => {
    // Prepara los datos para la solicitud combinando los datos comunes y el filtro de estados.
    const requestData = {
        ...commonRequestData, // Datos comunes para todas las solicitudes (e.g., tokens, configuración estándar).
        estados, // Filtro opcional de estados.
    };

    // Realiza la solicitud a la API para obtener la lista de cotizaciones.
    // El endpoint "cotizaciones/lista" se utiliza para obtener los datos necesarios.
    const response = await fetchData("cotizaciones/lista", requestData);

    // Retorna la respuesta completa de la API.
    return response;
};


export const obtener_CotizacionId = async ({ idConsecutivo }) => {
    // Prepara los datos para la solicitud combinando datos comunes y el identificador de cotización.
    const requestData = {
        ...commonRequestData, // Datos comunes para todas las solicitudes (e.g., tokens de autenticación, configuración estándar).
        idConsecutivo, // Identificador opcional para filtrar cotizaciones.
    };

    // Realiza la solicitud a la API para obtener la lista de cotizaciones.
    // El endpoint "cotizaciones/obtenerCotizacion" se utiliza para obtener la información requerida.
    const response = await fetchData("cotizaciones/obtenerCotizacion", requestData);

    // Retorna la respuesta completa obtenida de la API.
    return response;
};



// Función para obtener la cotización por ID desde la API
export const obtener_NumeroConsecutivo = async () => {
    try {
        // Prepara los datos de la solicitud con datos comunes y adicionales según sea necesario
        const requestData = {
            ...commonRequestData, // Incluye datos comunes como tokens de autenticación o configuraciones predeterminadas
        };

        // Realiza la solicitud al endpoint para obtener el número consecutivo de la cotización
        const response = await fetchData("cotizaciones/obtenerNumeroConsecutivo", requestData);

        return response; // Retorna los datos obtenidos
    } catch (error) {
        // Manejo de errores con mensajes detallados para depuración
        console.error("Error al obtener la cotización por ID:", error.message || error);
        throw new Error("No se pudo obtener la cotización. Verifique los detalles de la solicitud.");
    }
};



// Función para obtener los salones de un hotel desde la API
export const obtenerSalonesSegunHotel = async ({ idHotel }) => {
    try {
        // Prepara los datos de la solicitud, incluyendo información común y específica del hotel
        const requestData = {
            ...commonRequestData, // Incluye datos comunes como autenticación o configuraciones predeterminadas
            idHotel, // Agrega el ID del hotel como parámetro específico
        };

        // Realiza la solicitud al endpoint para obtener los salones del hotel
        const response = await fetchData("cotizaciones/obtenerSalonesSegunHotel", requestData);

        // Retorna la respuesta de la API con los datos obtenidos
        return response;
    } catch (error) {
        // Maneja y registra errores, proporcionando detalles para facilitar la depuración
        console.error("Error al obtener los salones del hotel:", error.message || error);
        throw new Error("No se pudo obtener la lista de salones. Verifique los detalles de la solicitud.");
    }
};


// Función para crear una cotización utilizando la API
export const crearCotizacionApi = async ({ datosCompletos }) => {
    try {
        // Prepara los datos de la solicitud combinando información común y específica
        const requestData = {
            ...commonRequestData, // Datos comunes como autenticación o configuraciones generales
            datosCompletos, // Información específica para la cotización, como detalles del hotel
        };

        // Realiza la solicitud al endpoint para crear la cotización
        const response = await fetchData("cotizaciones/crearCoticazion", requestData);

        // Retorna la respuesta de la API con los datos de la cotización
        return response;
    } catch (error) {
        // Maneja errores y los registra para facilitar la depuración
        console.error("Error al crear la cotización:", error.message || error);
        throw new Error("No se pudo crear la cotización. Verifique los detalles de la solicitud.");
    }
};



export const getCotizacionApi = async ({ id }) => {
    // Combina los datos comunes de la solicitud con el identificador de cotización.
    const requestData = {
        ...commonRequestData, // Datos generales, como autenticación y configuración predeterminada.
        id, // Identificador único para filtrar la cotización específica.
    };

    // Envía una solicitud a la API para obtener la cotización correspondiente al identificador proporcionado.
    // Utiliza el endpoint "cotizaciones/obtenerCotizacionId" para recuperar la información.
    const response = await fetchData("cotizaciones/obtenerCotizacionId", requestData);

    // Devuelve la respuesta completa obtenida de la API.
    return response;
};


export const deleteEliminarCotizacion = async ({ id }) => {
    // Prepara los datos de la solicitud combinando los datos comunes con el identificador de la cotización a eliminar
    const requestData = {
        ...commonRequestData, // Datos comunes para la solicitud, como autenticación y configuración predeterminada
        id, // El identificador único de la cotización que se va a eliminar
    };

    // Realiza la solicitud a la API utilizando el endpoint adecuado para eliminar la cotización especificada
    // En este caso, el endpoint "cotizaciones/eliminarCotizacion" se usa para eliminar la cotización con el id proporcionado
    const response = await fetchData("cotizaciones/eliminarCotizacion", requestData);

    // Devuelve la respuesta completa de la API, que indica el resultado de la eliminación
    return response;
};



export const cambioEstadoCotizacionApi = async ({ ids, estadosRelacionados, value, token }) => {
    // Prepara los datos de la solicitud combinando datos comunes con los específicos para el cambio de estado
    const requestData = {
        ...commonRequestData, // Datos comunes, como autenticación y configuración predeterminada
        ids, // Identificadores de las cotizaciones a actualizar
        estadosRelacionados, // Estados relacionados a los que se cambiarán las cotizaciones
        value, // Nuevo estado al que se cambiarán las cotizaciones
        token,
    };

    // Realiza la solicitud a la API para cambiar el estado de las cotizaciones especificadas
    // Utiliza el endpoint "cotizaciones/cambioEstadoCotizacionApi" con los datos preparados
    const response = await fetchData("cotizaciones/cambioEstadoCotizacionApi", requestData);

    // Devuelve la respuesta de la API, que contiene el resultado del cambio de estado
    return response;
};



export const obtener_CotizacionIdCliente = async ({ dato }) => {
    // Prepara los datos para la solicitud combinando datos comunes y el identificador de cotización.
    const requestData = {
        ...commonRequestData, // Datos comunes para todas las solicitudes (e.g., tokens de autenticación, configuración estándar).
        dato, // Identificador opcional para filtrar cotizaciones.
    };

    // Realiza la solicitud a la API para obtener la lista de cotizaciones.
    // El endpoint "cotizaciones/obtenerCotizacion" se utiliza para obtener la información requerida.
    const response = await fetchData("cotizaciones/obtenerCotizacionCliente", requestData);

    // Retorna la respuesta completa obtenida de la API.
    return response;
};
